import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import ReadingProgress from "./components/ReadingProgress";
import { QuoteFormProvider } from "./components/QuoteFormContext";

/**
 * The home page ships in the initial bundle — it is the LCP entry point for
 * almost all traffic. Every other route is split out so a first visit only
 * downloads the JavaScript it actually renders.
 */
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

/** Holds the page height while a route chunk loads so the swap causes no shift. */
const RouteFallback = () => <div className="min-h-screen bg-background" aria-hidden="true" />;

/*
 * `LazyMotion` + `domAnimation` loads only the animation and gesture features
 * this site actually uses (initial/animate/whileInView/exit) instead of the
 * whole framer-motion runtime. `strict` makes the build fail loudly if a
 * `motion.*` component sneaks back in, since those pull the full bundle.
 */
const App = () => (
  <LazyMotion features={domAnimation} strict>
    <QuoteFormProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ReadingProgress />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:slug" element={<LocationDetail />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ScrollTopButton />
      </BrowserRouter>
    </QuoteFormProvider>
  </LazyMotion>
);

export default App;
